import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Eyebrow,
  Heading,
  Skeleton,
  StatusBadge,
  Button,
  type StatusKey,
} from '@insolech/ui';
import {
  ClipboardList,
  Hammer,
  CheckCircle2,
  Truck,
  PackageCheck,
  ArrowRight,
} from 'lucide-react';
import { providerQueries, type ProviderActivityRow, type ProviderKpis } from '@insolech/sdk';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/features/auth/AuthContext';
import { supabase } from '@/lib/supabase';

const STATUS_TO_KEY: Record<string, StatusKey> = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  in_production: 'in_production',
  in_delivery: 'in_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
  returned: 'returned',
  on_hold: 'on_hold',
  assigned_to_provider: 'assigned_to_provider',
  quality_check: 'quality_check',
};

/**
 * /provider — provider home page. KPI cards + recent activity feed + quick
 * action to the pipeline. Phase 4d brief §2.
 */
export function ProviderDashboardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['provider', 'common']);
  const { profileLoading } = useAuth();

  const kpis = useQuery({
    ...providerQueries.providerKpisQuery(supabase, null),
    enabled: !profileLoading,
  });
  const activity = useQuery({
    ...providerQueries.providerActivityQuery(supabase, null, 12),
    enabled: !profileLoading,
  });

  const k = (kpis.data ?? null) as ProviderKpis | null;

  return (
    <AppShell>
      <main className="px-6 py-5 max-w-[1400px] mx-auto">
        <Eyebrow>{t('provider:title_dashboard')}</Eyebrow>
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <Heading as="h1" size="h3" className="mt-2">
              {t('provider:title_dashboard')}
            </Heading>
            <p className="text-text-secondary mt-1 text-sm">{t('provider:subtitle_dashboard')}</p>
          </div>
          <Button
            variant="primary"
            size="md"
            endIcon={<ArrowRight className="h-4 w-4 rtl-flip" />}
            onClick={() => navigate('/provider/orders')}
            data-testid="provider-cta-pipeline"
          >
            {t('provider:title_pipeline')}
          </Button>
        </div>

        <section
          aria-label="KPIs"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
        >
          <KpiCard
            icon={<ClipboardList className="h-5 w-5" />}
            label={t('provider:kpi_awaiting_acceptance')}
            value={k?.awaiting_acceptance}
            loading={kpis.isLoading}
            tone="brand"
            testId="kpi-awaiting-acceptance"
          />
          <KpiCard
            icon={<Hammer className="h-5 w-5" />}
            label={t('provider:kpi_in_production')}
            value={k?.in_production}
            loading={kpis.isLoading}
            tone="warn"
            testId="kpi-in-production"
          />
          <KpiCard
            icon={<CheckCircle2 className="h-5 w-5" />}
            label={t('provider:kpi_quality_check')}
            value={k?.quality_check}
            loading={kpis.isLoading}
            tone="info"
            testId="kpi-quality-check"
          />
          <KpiCard
            icon={<Truck className="h-5 w-5" />}
            label={t('provider:kpi_ready_to_ship')}
            value={k?.ready_to_ship}
            loading={kpis.isLoading}
            tone="success"
            testId="kpi-ready-to-ship"
          />
          <KpiCard
            icon={<PackageCheck className="h-5 w-5" />}
            label={t('provider:kpi_shipped_7d')}
            value={k?.shipped_7d}
            loading={kpis.isLoading}
            tone="muted"
            testId="kpi-shipped-7d"
          />
        </section>

        <section className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Recent activity</CardTitle>
            </CardHeader>
            <CardBody>
              {activity.isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : (activity.data ?? []).length === 0 ? (
                <p className="text-sm text-text-muted">
                  No transitions yet on lines assigned to you. New assignments will appear here.
                </p>
              ) : (
                <ul className="divide-y divide-border-subtle">
                  {(activity.data as ProviderActivityRow[]).map((row) => (
                    <li
                      key={row.history_id}
                      className="py-3 flex items-center gap-4 cursor-pointer hover:bg-sand-50/60 -mx-4 px-4 rounded"
                      onClick={() => navigate(`/provider/orders/${row.order_line_id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {row.patient_first_name ?? 'Patient'}
                          {row.patient_short_code ? (
                            <span className="ms-2 text-xs font-mono text-text-muted">
                              · {row.patient_short_code}
                            </span>
                          ) : null}
                        </p>
                        <p className="text-xs text-text-muted">
                          {row.order_number} · {formatDate(row.changed_at)} ·{' '}
                          <span className="text-text-secondary">
                            {row.actor_name ?? row.actor_role ?? 'system'}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge
                          status={(STATUS_TO_KEY[row.to_status] ?? 'draft') as StatusKey}
                          size="sm"
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </section>
      </main>
    </AppShell>
  );
}

function KpiCard({
  icon,
  label,
  value,
  loading,
  tone,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  loading: boolean;
  tone: 'brand' | 'warn' | 'info' | 'success' | 'muted';
  testId?: string;
}) {
  const toneClass = {
    brand: 'bg-brand-50 text-brand-700',
    warn: 'bg-warning-50 text-warning-700',
    info: 'bg-sky-50 text-sky-700',
    success: 'bg-success-50 text-success-700',
    muted: 'bg-sand-100 text-text-secondary',
  }[tone];
  return (
    <Card data-testid={testId}>
      <CardBody className="flex items-center gap-3">
        <div
          className={`h-10 w-10 rounded-md flex items-center justify-center ${toneClass}`}
          aria-hidden="true"
        >
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
          {loading ? (
            <Skeleton className="h-7 w-16 mt-1" />
          ) : (
            <p className="text-2xl font-semibold text-text-primary tabular">{value ?? 0}</p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}
